import { SocialLinks } from "~/types/UserTypes";
import { Community, Prisma, Role } from "@prisma/client";
import { ProfileEditRequiredData } from "~/components/rsvp/validators";
import { requiredRolesV2 } from "~/server/actions";

export const getNonEmptySocialLinks = (
    user: Prisma.UserGetPayload<{
        select: {
            socialLinks: true;
        };
    }>,
) => {
    if (!user.socialLinks) return [];
    return Object.values(user.socialLinks).filter(
        (link) => link !== "" && link !== null && link !== undefined,
    );
};
export const hasAnySocialLink = (
    user: Prisma.UserGetPayload<{
        select: {
            socialLinks: true;
        };
    }>,
) => {
    if (!user) return false;
    const socialLinks = getNonEmptySocialLinks(user);
    if (socialLinks.length === 0) return false;
    return true;
};

export enum MissingUserData {
    CONTACT_TELEGRAM = 1,
    CONTACT_WHATSAPP,
    CONTACT_ANY_CHAT,
    CONTACT_LINKEDIN,
    PROFILE_NAME,
    PROFILE_IMAGE,
    PROFILE_BIO,
    PROFILE_EMAIL,
    PROFILE_SOCIAL,
    PROFILE_PHONE_NUMBER,
}

/**
 * Remember to keep this function synced with the MissingProfileFieldsList.tsx
 */
export const getMissingProfileData = (
    community: Pick<
        Community,
        "requireHostProfileInformation" | "hostRequiredData"
    >,
    user: Prisma.UserGetPayload<{
        select: {
            name: true;
            image: true;
            bio: true;
            socialLinks: true;
            chatLinks: true;
            phone: true;
            role: true;
        };
    }>,
) => {
    if (user.role === Role.ANONYMOUS) return [];

    let missingData = [];
    if (community.requireHostProfileInformation) {
        if (!user.name) missingData.push(MissingUserData.PROFILE_NAME);
        if (!user.image) missingData.push(MissingUserData.PROFILE_IMAGE);
        if (!user.bio || user.bio.length < 30)
            missingData.push(MissingUserData.PROFILE_BIO);
        if (!hasAnySocialLink(user))
            missingData.push(MissingUserData.PROFILE_SOCIAL);
    }
    const requiredData = community.hostRequiredData;
    if (requiredData) {
        if (requiredData.telegram && !user.chatLinks?.telegram)
            missingData.push(MissingUserData.CONTACT_TELEGRAM);
        if (requiredData.whatsapp && !user.chatLinks?.whatsapp)
            missingData.push(MissingUserData.CONTACT_WHATSAPP);

        if (
            requiredData.anyChatContact &&
            !(user.chatLinks?.telegram || user.chatLinks?.whatsapp)
        ) {
            missingData.push(MissingUserData.CONTACT_ANY_CHAT);
        }

        if (requiredData.linkedin && !(user.socialLinks as SocialLinks)?.li) {
            missingData.push(MissingUserData.CONTACT_LINKEDIN);
        }

        if (requiredData.phoneNumber && !user.phone) {
            missingData.push(MissingUserData.PROFILE_PHONE_NUMBER);
        }
    }
    return missingData;
};

export const getMissingAttendeeProfileData = (
    community: Pick<Community, "attendeeRequiredData"> | null,
    user: Prisma.UserGetPayload<{
        select: {
            name: true;
            bio: true;
            image: true;
            chatLinks: true;
            socialLinks: true;
        };
    }>,
    isPreapprovedEvent: boolean = false,
) => {
    if (!community) return undefined;
    let missingData: ProfileEditRequiredData = {};

    const requiredData = community.attendeeRequiredData;
    if (requiredData) {
        if (requiredData.name && (!user.name || user.name?.length < 1))
            missingData.minNameLength = 1;
        if (requiredData.pfp && !user.image) missingData.imageRequired = true;

        if (
            requiredData.anyChatContact &&
            !(user.chatLinks?.telegram || user.chatLinks?.whatsapp)
        ) {
            missingData.chatLinks = true;
        }

        if (requiredData.anySocialLinks && !hasAnySocialLink(user)) {
            missingData.anySocialLinks = true;
        }
    }

    // Special case for pre-approved events, they require users to have a name
    if (isPreapprovedEvent && (!user.name || user.name.length < 1))
        missingData.minNameLength = 1;
    if (isPreapprovedEvent && (!user.bio || user.bio.length < 20))
        missingData.minBioLength = 20;

    return missingData;
};

export const anonymizeAttendeeNames = <
    T extends { attendees: { user: { name?: string | null } }[] }[],
>(
    events: T,
): T => {
    return events.map((event) => ({
        ...event,
        attendees: event.attendees.map((attendee) => {
            if (!attendee.user.name) return attendee;
            return {
                ...attendee,
                user: {
                    ...attendee.user,
                    email: attendee.user.name[0] ?? null,
                },
            };
        }),
    })) as T;
};
